/**
 * Gedeelde bouwstenen van de overlegmodule: stoplicht, persoonkiezer, actielijst, issues, besluiten,
 * headlines, Rocks, scorecard-strook, invulblok en de ratingdialoog (chip-rij 1–10, anoniem).
 */
import React, { useMemo, useRef, useState } from 'react';
import {
  Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton,
  LinearProgress, MenuItem, Stack, Switch, TextField, Tooltip, Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { bevestig } from '../../utils/bevestig';
import { biedOngedaanAan } from '../../utils/ongedaan';
import { contextTelling, eersteRegel } from './IssueContextPaneel';

export const STATUS_KLEUR = {
  groen: { bg: '#dcfce7', fg: '#166534' }, oranje: { bg: '#ffedd5', fg: '#9a3412' },
  rood: { bg: '#fee2e2', fg: '#991b1b' }, leeg: { bg: '#f1f5f9', fg: '#64748b' },
};
export const SESSIE_STATUS = {
  gepland: { label: 'Voorbereiding', bg: '#e0e7ff', fg: '#3730a3' }, pakket_verstuurd: { label: 'Voorbereiding · pakket verstuurd', bg: '#e0f2fe', fg: '#075985' },
  bezig: { label: 'Bezig', bg: '#dcfce7', fg: '#166534' }, beoordelen: { label: 'Beoordelen', bg: '#fef3c7', fg: '#92400e' },
  afgesloten: { label: 'Afgesloten', bg: '#f1f5f9', fg: '#475569' },
  overgeslagen: { label: 'Overgeslagen', bg: '#f8fafc', fg: '#94a3b8' },
};

/** Twee kaarten naast elkaar zonder overloop: minmax(0, 1fr) laat een kolom smaller worden dan zijn langste regel. */
export const TWEE_KOLOMMEN = { xs: 'minmax(0, 1fr)', lg: 'repeat(2, minmax(0, 1fr))' };
/** Knoppen in een rij: nooit over twee regels. */
const KNOP_EEN_REGEL = { whiteSpace: 'nowrap', flexShrink: 0 };

export const naam = (email) => (email || '').split('@')[0].split('.').map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join(' ') || '—';
export const datumKort = (iso) => (iso ? new Date(iso.length === 10 ? `${iso}T12:00:00` : iso).toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' }) : '');
export const tijdKort = (iso) => (iso ? new Date(iso).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' }) : '');
export const formatWaarde = (w, eenheid) => {
  if (w == null) return '–';
  if (eenheid === 'eur') return `€ ${Math.round(w).toLocaleString('nl-NL')}`;
  if (eenheid === 'pct') return `${Math.round(w * 10) / 10}%`;
  if (eenheid === 'score') return String(Math.round(w * 10) / 10).replace('.', ',');
  return String(Math.round(w * 100) / 100).replace('.', ',');
};

export function Stoplicht({ status, label }) {
  const k = STATUS_KLEUR[status] || STATUS_KLEUR.leeg;
  return <Chip size="small" label={label || status} sx={{ height: 20, fontSize: 11, fontWeight: 800, bgcolor: k.bg, color: k.fg, textTransform: 'uppercase', letterSpacing: '0.04em' }} />;
}

export function SessieStatusChip({ status }) {
  const s = SESSIE_STATUS[status] || SESSIE_STATUS.gepland;
  return <Chip size="small" label={s.label} sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: s.bg, color: s.fg }} />;
}

export function PersoonSelect({ personen, value, onChange, label = 'Wie', size = 'small', sx, allowLeeg = false }) {
  const opties = useMemo(() => {
    const lijst = [...(personen || [])];
    if (value && !lijst.some((p) => p.email === value)) lijst.push({ email: value, naam: naam(value) });
    return lijst;
  }, [personen, value]);
  return (
    <TextField select size={size} label={label} value={value || ''} onChange={(e) => onChange(e.target.value)} sx={{ minWidth: 180, ...sx }}>
      {allowLeeg && <MenuItem value="">(niemand)</MenuItem>}
      {opties.map((p) => <MenuItem key={p.email} value={p.email}>{p.naam || naam(p.email)}</MenuItem>)}
    </TextField>
  );
}

export const MAX_TITEL = 500;
export const MAX_OMSCHRIJVING = 4000;

/**
 * Bewerking van een actie opslaan. Draag je je eigen actie over aan iemand anders, dan vragen we eerst om
 * bevestiging en bieden we geen ongedaan maken aan (zonder schrijfrecht mag je hem daarna niet meer wijzigen).
 * Anders: toast met een knop die precies deze bewerking terugdraait (eigenKnop, niet de globale laatste).
 */
export async function bewaarActie(todo, wijziging, { mijnEmail, patch, herlaad, personen }) {
  const van = (todo.eigenaar_email || '').toLowerCase();
  const naar = (wijziging.eigenaar_email || '').toLowerCase();
  if (naar && naar !== van && !mijnEmail) {   // wie ben ik nog onbekend → niet overdragen zonder bevestiging (Codex)
    toast.error('Even geduld: je gegevens worden nog geladen. Probeer het zo opnieuw.');
    throw new Error('identiteit onbekend');
  }
  const overdracht = naar && naar !== van && van === mijnEmail.toLowerCase();
  if (overdracht) {
    const wie = (personen || []).find((p) => p.email === naar)?.naam || naam(naar);
    const ok = await bevestig({ titel: 'Actie overdragen?', tekst: `Je draagt "${todo.titel}" over aan ${wie}. Daarna kunnen alleen ${wie} en de voorzitter hem nog aanpassen.`, knop: 'Overdragen' });
    if (!ok) throw new Error('geannuleerd');
  }
  try { await patch(todo.id, wijziging); } catch (e) { toast.error(e.message || 'Opslaan mislukt'); throw e; }
  herlaad();
  if (overdracht) { toast.success('Actie overgedragen'); return; }
  const vorig = { titel: todo.titel, omschrijving: todo.omschrijving || '', eigenaar_email: todo.eigenaar_email, deadline: todo.deadline };
  biedOngedaanAan('Actie bijgewerkt', async () => { await patch(todo.id, vorig); herlaad(); }, { eigenKnop: true });
}

const plusWeek = () => new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);

/** Actie bewerken: wat, toelichting, wie, wanneer. Opslaan geeft de wijziging door; de ouder biedt ongedaan maken aan. */
export function TodoDialog({ todo, personen, onClose, onOpslaan }) {
  const [titel, setTitel] = useState(todo?.titel || '');
  const [omschrijving, setOmschrijving] = useState(todo?.omschrijving || '');
  const [eigenaar, setEigenaar] = useState(todo?.eigenaar_email || '');
  const [deadline, setDeadline] = useState(todo?.deadline || plusWeek());
  const [bezig, setBezig] = useState(false);
  const opslaan = async () => {
    if (!titel.trim() || !eigenaar) return;
    setBezig(true);
    try { await onOpslaan({ titel: titel.trim(), omschrijving: omschrijving.trim(), eigenaar_email: eigenaar, deadline }); onClose(); } catch { /* toast via ouder */ } finally { setBezig(false); }
  };
  return (
    <Dialog open={!!todo} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 800 }}>Actie bewerken</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField label="Wat" value={titel} onChange={(e) => setTitel(e.target.value)} fullWidth autoFocus multiline maxRows={3} inputProps={{ maxLength: MAX_TITEL }} />
          <TextField label="Omschrijving" placeholder="Achtergrond, afspraken, wat 'klaar' betekent…" value={omschrijving} onChange={(e) => setOmschrijving(e.target.value)} fullWidth multiline minRows={3}
            inputProps={{ maxLength: MAX_OMSCHRIJVING }} helperText={omschrijving.length > MAX_OMSCHRIJVING - 400 ? `${omschrijving.length} / ${MAX_OMSCHRIJVING} tekens` : ' '} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <PersoonSelect personen={personen} value={eigenaar} onChange={setEigenaar} size="medium" sx={{ flex: 1 }} />
            <TextField type="date" label="Wanneer" value={deadline} onChange={(e) => setDeadline(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: { sm: 180 } }} />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Annuleren</Button>
        <Button variant="contained" disableElevation disabled={bezig || !titel.trim() || !eigenaar} onClick={opslaan} sx={{ textTransform: 'none', fontWeight: 700 }}>Opslaan</Button>
      </DialogActions>
    </Dialog>
  );
}

/** Actielijst: te laat bovenaan, afvinken, bewerken (potlood), nieuwe actie met verplichte eigenaar + deadline (standaard +7 dagen). */
export function TodoLijst({ todos, personen, onToggle, onNieuw, onBewerk, mijnEmail, compact = false, standaardEigenaar }) {
  const [titel, setTitel] = useState('');
  const [omschrijving, setOmschrijving] = useState('');
  const [metOmschrijving, setMetOmschrijving] = useState(false);
  const [eigenaar, setEigenaar] = useState(standaardEigenaar || mijnEmail || '');
  const [deadline, setDeadline] = useState(plusWeek);
  const [bewerkt, setBewerkt] = useState(null);
  const voegToe = async () => {
    if (!titel.trim() || !eigenaar) return;
    await onNieuw({ titel: titel.trim(), omschrijving: omschrijving.trim(), eigenaar_email: eigenaar, deadline });
    setTitel(''); setOmschrijving(''); setMetOmschrijving(false);
  };
  const magBewerken = (t) => !!onBewerk && !t.beperkt;
  return (
    <Box>
      <Stack spacing={0.5}>
        {(todos || []).map((t) => (
          <Stack key={t.id} direction="row" alignItems="flex-start" spacing={1} sx={{ px: 1, py: 0.5, borderRadius: 1.5, bgcolor: t.te_laat ? '#fff7ed' : '#fff', border: '1px solid', borderColor: t.te_laat ? '#fed7aa' : '#e2e8f0', '&:hover .todo-bewerk': { opacity: 1 } }}>
            <Checkbox size="small" checked={t.status === 'gedaan'} onChange={() => onToggle(t)} sx={{ p: 0.25, mt: 0.25 }} />
            <Box sx={{ flex: 1, minWidth: 0, cursor: magBewerken(t) ? 'pointer' : 'default' }} onClick={() => magBewerken(t) && setBewerkt(t)}>
              <Typography sx={{ fontSize: 13.5, fontWeight: 600, lineHeight: 1.6, overflowWrap: 'anywhere', textDecoration: t.status === 'gedaan' ? 'line-through' : 'none', color: t.status === 'gedaan' ? '#94a3b8' : '#1e293b' }}>
                {t.titel}
                {t.type_naam && !compact && <Box component="span" sx={{ ml: 0.75, fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>{t.type_naam}</Box>}
              </Typography>
              {t.omschrijving && (
                <Typography sx={{ fontSize: 12.5, color: '#64748b', whiteSpace: 'pre-wrap', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{t.omschrijving}</Typography>
              )}
            </Box>
            <Typography sx={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap', mt: 0.4 }}>{naam(t.eigenaar_email)}</Typography>
            <Chip size="small" label={t.te_laat ? `te laat · ${datumKort(t.deadline)}` : datumKort(t.deadline)}
              sx={{ height: 20, mt: 0.3, fontSize: 11, fontWeight: 700, bgcolor: t.te_laat ? '#fee2e2' : '#f1f5f9', color: t.te_laat ? '#991b1b' : '#475569' }} />
            {magBewerken(t) && (
              <Tooltip title="Bewerken">
                <IconButton size="small" className="todo-bewerk" aria-label={`Bewerk ${t.titel}`} onClick={() => setBewerkt(t)} sx={{ opacity: { xs: 1, md: 0.35 }, p: 0.25 }}>
                  <EditOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        ))}
        {(!todos || todos.length === 0) && <Typography sx={{ fontSize: 13, color: '#94a3b8', px: 1 }}>Geen open acties.</Typography>}
      </Stack>
      {onNieuw && (
        <Stack spacing={1} sx={{ mt: 1.5 }}>
          <TextField size="small" fullWidth placeholder="Nieuwe actie: wie doet wat…" value={titel} onChange={(e) => setTitel(e.target.value)} inputProps={{ maxLength: MAX_TITEL }}
            onKeyDown={(e) => { if (e.key === 'Enter' && !metOmschrijving) voegToe(); }} />
          {metOmschrijving && (
            <TextField size="small" fullWidth multiline minRows={2} placeholder="Omschrijving (optioneel)" value={omschrijving} onChange={(e) => setOmschrijving(e.target.value)} autoFocus inputProps={{ maxLength: MAX_OMSCHRIJVING }} />
          )}
          {/* Eén rij: 'Omschrijving' links, Wie · Wanneer · Toevoegen rechts en even hoog; smaller → netjes onder elkaar. */}
          <Stack direction={{ xs: 'column', sm: 'row' }} useFlexGap flexWrap="wrap" gap={1} alignItems={{ xs: 'stretch', sm: 'center' }} data-testid="todo-invoerrij">
            {!metOmschrijving && (
              <Button size="small" onClick={() => setMetOmschrijving(true)} sx={{ ...KNOP_EEN_REGEL, textTransform: 'none', color: '#64748b', alignSelf: { xs: 'flex-start', sm: 'center' } }}>＋ Omschrijving</Button>
            )}
            <PersoonSelect personen={personen} value={eigenaar} onChange={setEigenaar} sx={{ minWidth: 0, flex: { sm: '1 1 150px' }, maxWidth: { sm: 220 }, ml: { sm: 'auto' } }} />
            <TextField size="small" type="date" label="Wanneer" value={deadline} onChange={(e) => setDeadline(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: { sm: 150 }, flexShrink: 0 }} />
            <Button size="small" variant="contained" disableElevation disabled={!titel.trim() || !eigenaar} onClick={voegToe} sx={{ ...KNOP_EEN_REGEL, textTransform: 'none', fontWeight: 700, alignSelf: { xs: 'flex-start', sm: 'center' } }}>Toevoegen</Button>
          </Stack>
        </Stack>
      )}
      {bewerkt && <TodoDialog todo={bewerkt} personen={personen} onClose={() => setBewerkt(null)} onOpslaan={(w) => onBewerk(bewerkt, w)} />}
    </Box>
  );
}

/** Issues op prioriteit; formuleer als vraag; opgelost → vraagt om de acties die eruit volgen (via onOpgelost). */
/** Klik op de titel (onOpen) opent het issue-paneel; `context` = tellingen per issue-id ({bijlage, mail, link}). */
export function IssueLijst({ issues, personen, onNieuw, onStatus, onOmhoog, onVerwijder, onOpen, context, mijnEmail, magSchrijven = true }) {
  const [titel, setTitel] = useState('');
  const [eigenaar, setEigenaar] = useState(mijnEmail || '');
  // Opslaan kan even duren: knop uit + 'Toevoegen…', het issue staat meteen (grijs) in de lijst en een tweede
  // Enter/klik doet niets (Joris 2 okt: dubbel opgeslagen). De server vangt een herhaalde submit ook nog af.
  const [wachtend, setWachtend] = useState(null);
  const bezig = useRef(false);
  const voegToe = async () => {
    const tekst = titel.trim();
    if (!tekst || bezig.current) return;
    bezig.current = true;
    setWachtend({ titel: tekst, eigenaar_email: eigenaar || null });
    setTitel('');
    try {
      await onNieuw({ titel: tekst, eigenaar_email: eigenaar || null });
    } catch (e) {
      setTitel(tekst);   // niets kwijt: de tekst staat terug in het veld
    } finally {
      bezig.current = false;
      setWachtend(null);
    }
  };
  const lijst = issues || [];
  const toonWachtend = wachtend && !lijst.some((i) => (i.titel || '').trim().toLowerCase() === wachtend.titel.toLowerCase());
  return (
    <Box>
      <Stack spacing={0.5}>
        {lijst.map((i, idx) => (
          <Stack key={i.id} direction="row" alignItems="center" useFlexGap flexWrap="wrap" columnGap={1} rowGap={0.25} data-testid="issue-rij"
            sx={{ px: 1, py: 0.5, borderRadius: 1.5, bgcolor: '#fff', border: '1px solid #e2e8f0', minWidth: 0 }}>
            <Typography sx={{ width: 22, flexShrink: 0, fontSize: 12, fontWeight: 800, color: '#94a3b8' }}>{idx + 1}.</Typography>
            <Box sx={{ flex: '1 1 180px', minWidth: 0 }}>
              {onOpen ? (
                <Typography component="button" type="button" onClick={() => onOpen(i)}
                  sx={{ p: 0, border: 0, bgcolor: 'transparent', cursor: 'pointer', textAlign: 'left', font: 'inherit', fontSize: 13.5, fontWeight: 600, color: '#1e293b', maxWidth: '100%', overflowWrap: 'anywhere', '&:hover': { textDecoration: 'underline' } }}>
                  {i.titel}
                </Typography>
              ) : <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: '#1e293b', overflowWrap: 'anywhere' }}>{i.titel}</Typography>}
              {(eersteRegel(i.toelichting) || contextTelling(context?.[i.id])) && (
                <Typography noWrap sx={{ fontSize: 12, color: '#94a3b8' }}>
                  {[contextTelling(context?.[i.id]), eersteRegel(i.toelichting)].filter(Boolean).join(' — ')}
                </Typography>
              )}
              {i.bron !== 'handmatig' && <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>automatisch uit {i.bron}{i.bron_periode ? ` · ${i.bron_periode}` : ''}</Typography>}
            </Box>
            {/* Rechterkant blijft bij elkaar; past hij niet meer naast de titel, dan schuift hij als geheel naar de volgende regel. */}
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0, maxWidth: '100%', ml: 'auto' }}>
              {i.eigenaar_email && <Typography noWrap title={naam(i.eigenaar_email)} sx={{ fontSize: 12, color: '#64748b', minWidth: 0, maxWidth: 140, mr: 0.5 }}>{naam(i.eigenaar_email)}</Typography>}
              {magSchrijven && onOmhoog && idx > 0 && (
                <Tooltip title="Hoger op de lijst"><IconButton size="small" aria-label={`Hoger: ${i.titel}`} onClick={() => onOmhoog(i)}><ArrowUpwardIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
              )}
              {/* Geen fragment als Stack-kind (spacing + fragment blankt de pagina): elke knop een losse child. */}
              {magSchrijven && onStatus && (
                <Button size="small" onClick={() => onStatus(i, 'opgelost')} sx={{ ...KNOP_EEN_REGEL, textTransform: 'none', fontWeight: 700, minWidth: 0 }}>Opgelost</Button>
              )}
              {magSchrijven && onStatus && (
                <Button size="small" color="inherit" onClick={() => onStatus(i, 'geparkeerd')} sx={{ ...KNOP_EEN_REGEL, textTransform: 'none', minWidth: 0, color: '#94a3b8' }}>Parkeer</Button>
              )}
              {magSchrijven && onVerwijder && (
                <Tooltip title="Verwijderen (foutje of dubbel)"><IconButton size="small" aria-label={`Verwijder issue ${i.titel}`} onClick={() => onVerwijder(i)} sx={{ flexShrink: 0 }}><DeleteOutlineIcon sx={{ fontSize: 16, color: '#94a3b8' }} /></IconButton></Tooltip>
              )}
            </Stack>
          </Stack>
        ))}
        {toonWachtend && (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1, py: 0.5, borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px dashed #cbd5e1' }} data-testid="issue-wachtend">
            <Typography sx={{ width: 22, fontSize: 12, fontWeight: 800, color: '#cbd5e1' }}>{lijst.length + 1}.</Typography>
            <Typography sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere', fontSize: 13.5, fontWeight: 600, color: '#94a3b8' }}>{wachtend.titel}</Typography>
            <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>opslaan…</Typography>
          </Stack>
        )}
        {lijst.length === 0 && !toonWachtend && <Typography sx={{ fontSize: 13, color: '#94a3b8', px: 1 }}>Geen open issues.</Typography>}
      </Stack>
      {magSchrijven && onNieuw && (
        <Stack direction={{ xs: 'column', sm: 'row' }} useFlexGap flexWrap="wrap" gap={1} sx={{ mt: 1.25 }} alignItems={{ xs: 'stretch', sm: 'center' }} data-testid="issue-invoerrij">
          <TextField size="small" placeholder="Nieuw issue, als vraag: 'Hoe krijgen we …?'" value={titel} onChange={(e) => setTitel(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); voegToe(); } }} sx={{ flex: { sm: '999 1 240px' }, minWidth: 0 }} />
          <PersoonSelect personen={personen} value={eigenaar} onChange={setEigenaar} allowLeeg label="Eigenaar" sx={{ minWidth: 0, flex: { sm: '1 1 150px' }, maxWidth: { sm: 220 } }} />
          <Button size="small" variant="outlined" disabled={!titel.trim() || !!wachtend} onClick={voegToe} sx={{ ...KNOP_EEN_REGEL, textTransform: 'none', fontWeight: 700, alignSelf: { xs: 'flex-start', sm: 'center' } }}>{wachtend ? 'Toevoegen…' : 'Toevoegen'}</Button>
        </Stack>
      )}
    </Box>
  );
}

/** Besluit in één zin, precies één eigenaar, omkeerbaar/onomkeerbaar, met de acties die eruit volgen. */
export function BesluitDialog({ open, onClose, onOpslaan, personen, mijnEmail, issue }) {
  const [titel, setTitel] = useState('');
  const [rationale, setRationale] = useState('');
  const [eigenaar, setEigenaar] = useState(mijnEmail || '');
  const [onomkeerbaar, setOnomkeerbaar] = useState(false);
  const [todos, setTodos] = useState([]);
  const [tTitel, setTTitel] = useState('');
  const [tEig, setTEig] = useState(mijnEmail || '');
  const [tDl, setTDl] = useState(() => new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10));
  const reset = () => { setTitel(''); setRationale(''); setOnomkeerbaar(false); setTodos([]); setTTitel(''); };
  const opslaan = async () => {
    await onOpslaan({ titel: titel.trim(), rationale: rationale.trim() || null, eigenaar_email: eigenaar, soort: onomkeerbaar ? 'onomkeerbaar' : 'omkeerbaar', issue_id: issue?.id || null, todos });
    reset(); onClose();
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>Besluit vastleggen{issue ? ` · lost op: ${issue.titel}` : ''}</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ mt: 0.5 }}>
          <TextField autoFocus label="Het besluit in één zin" value={titel} onChange={(e) => setTitel(e.target.value)} fullWidth />
          <TextField label="Waarom (kort)" value={rationale} onChange={(e) => setRationale(e.target.value)} fullWidth multiline minRows={2} />
          <Stack direction="row" spacing={2} alignItems="center">
            <PersoonSelect personen={personen} value={eigenaar} onChange={setEigenaar} label="Eigenaar (één)" />
            <FormControlLabel control={<Switch checked={onomkeerbaar} onChange={(e) => setOnomkeerbaar(e.target.checked)} />} label={<Typography variant="body2">Onomkeerbaar</Typography>} />
          </Stack>
          <Typography sx={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748b', mt: 1 }}>Acties die eruit volgen</Typography>
          {todos.map((t, i) => (
            <Stack key={i} direction="row" spacing={1} alignItems="center">
              <Typography sx={{ flex: 1, fontSize: 13 }}>{t.titel}: {naam(t.eigenaar_email)}, {datumKort(t.deadline)}</Typography>
              <IconButton size="small" onClick={() => setTodos((p) => p.filter((_, j) => j !== i))}><DeleteOutlineIcon sx={{ fontSize: 16 }} /></IconButton>
            </Stack>
          ))}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
            <TextField size="small" placeholder="Actie" value={tTitel} onChange={(e) => setTTitel(e.target.value)} sx={{ flex: 1 }} />
            <PersoonSelect personen={personen} value={tEig} onChange={setTEig} />
            <TextField size="small" type="date" value={tDl} onChange={(e) => setTDl(e.target.value)} sx={{ width: 150 }} />
            <Button size="small" disabled={!tTitel.trim() || !tEig} onClick={() => { setTodos((p) => [...p, { titel: tTitel.trim(), eigenaar_email: tEig, deadline: tDl }]); setTTitel(''); }} sx={{ textTransform: 'none', fontWeight: 700 }}>＋</Button>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Annuleren</Button>
        <Button variant="contained" disableElevation disabled={!titel.trim() || !eigenaar} onClick={opslaan} sx={{ textTransform: 'none', fontWeight: 700 }}>Vastleggen</Button>
      </DialogActions>
    </Dialog>
  );
}

/** Rating 1–10 in één klik. Alleen aggregaten komen terug; niemand ziet wie wat gaf. */
export function RatingDialog({ open, mijnRating, onKies, onLater, titel }) {
  const [gekozen, setGekozen] = useState(mijnRating || null);
  const [opm, setOpm] = useState('');
  return (
    <Dialog open={open} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>Hoe was dit overleg?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{titel || 'Eén klik. Je cijfer is anoniem: alleen het gemiddelde en de spreiding zijn zichtbaar.'}</Typography>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
            <Chip key={n} label={n} clickable onClick={() => setGekozen(n)} sx={{ fontWeight: 800, minWidth: 40, bgcolor: gekozen === n ? '#4338ca' : '#f1f5f9', color: gekozen === n ? '#fff' : '#1e293b' }} />
          ))}
        </Stack>
        <TextField size="small" fullWidth placeholder="Opmerking (optioneel)" value={opm} onChange={(e) => setOpm(e.target.value)} sx={{ mt: 2 }} />
      </DialogContent>
      <DialogActions>
        {onLater && <Button onClick={onLater} sx={{ textTransform: 'none', color: '#94a3b8' }}>Later</Button>}
        <Button variant="contained" disableElevation disabled={!gekozen} onClick={() => onKies(gekozen, opm)} sx={{ textTransform: 'none', fontWeight: 700 }}>Versturen</Button>
      </DialogActions>
    </Dialog>
  );
}

/** Scorecard-strook: de cijfers van dit overleg voor de periode van de sessie, handmatig in te vullen. */
const RITME_LABEL = { week: 'Weekcijfers', maand: 'Maandcijfers', kwartaal: 'Kwartaalcijfers' };

function Trend({ m }) {
  const reeks = (m.reeks || []).filter((r) => r.waarde != null);
  if (reeks.length < 2) return null;
  const vorige = reeks[reeks.length - 2].waarde;
  const nu = reeks[reeks.length - 1].waarde;
  if (vorige === nu) return <Box component="span" sx={{ ml: 0.75, fontSize: 13, color: '#94a3b8' }}>→</Box>;
  const beter = (m.richting === 'lager') ? nu < vorige : nu > vorige;
  return <Tooltip title={`vorige periode ${formatWaarde(vorige, m.eenheid)}`}><Box component="span" sx={{ ml: 0.75, fontSize: 13, fontWeight: 800, color: beter ? '#15803d' : '#b91c1c' }}>{nu > vorige ? '↑' : '↓'}</Box></Tooltip>;
}

const maandLabel = (p) => { if (!p || p.length < 7) return p || ''; const m = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec']; return `${m[Number(p.slice(5, 7)) - 1]} ${p.slice(2, 4)}`; };

/** Evaluaties achter de score: laatste evaluaties met klant, project, score en link naar dashboard en Airtable. */
export function EvaluatiesDialog({ open, onClose, snapshot, periode }) {
  const s = snapshot || {};
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>Klantevaluaties · {maandLabel(periode)}</DialogTitle>
      <DialogContent>
        <Stack direction="row" spacing={3} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <Box><Typography sx={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', color: '#64748b' }}>Deze maand</Typography><Typography sx={{ fontSize: 22, fontWeight: 800 }}>{s.n ? formatWaarde(s.gemiddelde ?? null, 'score') : '–'} <Box component="span" sx={{ fontSize: 12, color: '#94a3b8' }}>{s.n || 0} evaluaties</Box></Typography><Typography sx={{ fontSize: 12, color: '#64748b' }}>proces {formatWaarde(s.proces, 'score')} · briefing {formatWaarde(s.briefing, 'score')}</Typography></Box>
          <Box><Typography sx={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', color: '#64748b' }}>Afgelopen 12 maanden</Typography><Typography sx={{ fontSize: 22, fontWeight: 800 }}>{formatWaarde(s.jaar_gem, 'score')} <Box component="span" sx={{ fontSize: 12, color: '#94a3b8' }}>{s.jaar_n || 0} evaluaties</Box></Typography><Typography sx={{ fontSize: 12, color: '#64748b' }}>proces {formatWaarde(s.jaar_proces, 'score')} · briefing {formatWaarde(s.jaar_briefing, 'score')}</Typography></Box>
          {s.onder_4 > 0 && <Chip label={`${s.onder_4} onder de 4 · klant bellen`} sx={{ fontWeight: 800, bgcolor: '#fee2e2', color: '#991b1b', alignSelf: 'center' }} />}
        </Stack>
        <Stack spacing={0.75}>
          {(s.laatste || []).map((e) => (
            <Box key={e.id} sx={{ p: 1.25, borderRadius: 1.5, border: '1px solid #e2e8f0', borderLeft: `4px solid ${(e.gemiddelde ?? 5) < 4 ? '#dc2626' : ((e.gemiddelde ?? 5) < 4.5 ? '#f59e0b' : '#16a34a')}` }}>
              <Stack direction="row" spacing={1} alignItems="baseline">
                <Typography sx={{ fontSize: 18, fontWeight: 800, width: 44 }}>{formatWaarde(e.gemiddelde, 'score')}</Typography>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{e.klant || '–'} <Box component="span" sx={{ fontWeight: 500, color: '#64748b' }}>· {e.project || ''}</Box></Typography>
                  <Typography sx={{ fontSize: 12, color: '#64748b' }}>{e.datum} · proces {formatWaarde(e.proces, 'score')} · briefing {formatWaarde(e.briefing, 'score')}{e.opmerking ? ` · “${e.opmerking}”` : ''}</Typography>
                </Box>
                <Button size="small" href={e.dashboard_url} sx={{ textTransform: 'none', fontWeight: 700, minWidth: 0 }}>Dashboard</Button>
                <Button size="small" href={e.airtable_url} target="_blank" rel="noreferrer" sx={{ textTransform: 'none', fontWeight: 700, minWidth: 0 }}>Airtable ↗</Button>
              </Stack>
            </Box>
          ))}
          {(s.laatste || []).length === 0 && <Typography sx={{ fontSize: 13, color: '#94a3b8' }}>Geen evaluaties in deze periode.</Typography>}
        </Stack>
      </DialogContent>
      <DialogActions><Button href="/enquete-dashboard" sx={{ textTransform: 'none' }}>Alle evaluaties</Button><Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 700 }}>Sluiten</Button></DialogActions>
    </Dialog>
  );
}

// Korte uitleg onder de naam van een cijfer dat zonder context niet te lezen is (rustig: één grijze regel).
export const CIJFER_UITLEG = {
  ohw_stand: 'Onderhanden werk: gemaakt werk dat nog niet gefactureerd is',
};

// "berekend 14:05" onder een berekend cijfer (berekend_at is naïef UTC met Z).
export function berekendLabel(iso) {
  if (!iso) return ' · berekend';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return ' · berekend';
  const tijd = d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Amsterdam' });
  const dag = d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', timeZone: 'Europe/Amsterdam' });
  const vandaag = new Date().toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', timeZone: 'Europe/Amsterdam' });
  return ` · berekend ${dag === vandaag ? tijd : `${dag} ${tijd}`}`;
}

/** Waarde van vóór de lopende herberekening (de pagina toont eerst de laatst bekende cijfers). */
export function isOudeWaarde(m, bijwerken, bijwerkenSinds) {
  if (!bijwerken || !bijwerkenSinds || !m || m.handmatig || m.waarde == null || !m.berekend_at) return false;
  const b = new Date(m.berekend_at).getTime();
  const s = new Date(bijwerkenSinds).getTime();
  return !Number.isNaN(b) && !Number.isNaN(s) && b < s;
}

/** OHW per maand tegenover de target BBI (Joris 16 okt): groen ≥ 100%, oranje 80–100%, rood < 80%; zelfde regel als
 * de engine (overleg_regels.ohw_status). Toekomstige maanden krijgen dezelfde drempels met 'nog in te vullen'. */
export function ohwPct(km) {
  // Zelf rekenen, niet de (al afgeronde) pct uit het werkboek: aan de grens moet de kleur kloppen (= engine).
  if (!km || km.bbi == null || !(Number(km.target_bbi) > 0)) return null;   // zonder bruikbare target geen kleur
  return (100 * Number(km.bbi)) / Number(km.target_bbi);
}
export function ohwStatus(km) {
  const p = ohwPct(km);
  if (p == null) return 'leeg';
  return p >= 100 ? 'groen' : (p >= 80 ? 'oranje' : 'rood');
}

/** Week-cijfer t/m gisteren (geschreven uren, declarabiliteit): hoe ver deze week is, en de vorige, complete week
 * met zijn eigen stoplicht — die is de beoordeelbare (Joris 16 okt: "W42 staat op 28%, omdat die week nog loopt"). */
export function WeekVoortgang({ m }) {
  const vg = m.voortgang || {};
  const v = m.vorige;
  const kleur = (st) => (STATUS_KLEUR[st] || STATUS_KLEUR.leeg).fg;
  const vWeek = v?.voortgang?.week || (v?.periode ? `W${Number(v.periode.split('-W')[1])}` : '');
  const vLoopt = v?.voortgang && !v.voortgang.compleet;
  // Kort en rustig (Joris 16 okt): "W42 · loopt nog" en een ingesprongen subregel "vorige week (W41)".
  return (
    <Box sx={{ mt: 0.25 }}>
      {vg.label && <Tooltip title={vg.detail || ''}><Typography sx={{ fontSize: 11.5, color: vg.compleet ? '#64748b' : '#9a3412', width: 'fit-content' }}>{vg.label}</Typography></Tooltip>}
      {v && (
        <Typography sx={{ fontSize: 11, color: '#475569', pl: 1.5 }}>
          <Box component="span" sx={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', bgcolor: kleur(v.status), mr: 0.5, verticalAlign: 'middle' }} />
          vorige week{vWeek ? ` (${vWeek})` : ''}{vLoopt ? ' · loopt nog' : ''}: <b>{formatWaarde(v.waarde, m.eenheid)}</b>
        </Typography>
      )}
    </Box>
  );
}

export function ScorecardStrook({ measurables, onWaarde, magInvullen, bijwerken = false, bijwerkenSinds = null }) {
  const [bewerk, setBewerk] = useState({});
  const [evalOpen, setEvalOpen] = useState(null);
  if (!measurables || measurables.length === 0) return <Typography sx={{ fontSize: 13, color: '#94a3b8' }}>Geen cijfers gekoppeld aan dit overleg.</Typography>;
  const groepen = ['week', 'maand', 'kwartaal'].map((r) => [r, measurables.filter((m) => (m.ritme || 'week') === r)]).filter(([, l]) => l.length);
  return (
    <Stack spacing={1.5}>
      {bijwerken && (
        <Box role="status">
          <Typography sx={{ fontSize: 12, color: '#64748b', mb: 0.5 }}>Cijfers worden bijgewerkt…</Typography>
          <LinearProgress sx={{ height: 2, borderRadius: 1 }} />
        </Box>
      )}
      {groepen.map(([ritme, lijst]) => (
        <Box key={ritme}>
          {groepen.length > 1 && <Typography sx={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#94a3b8', mb: 0.5 }}>{RITME_LABEL[ritme]}{new Set(lijst.map((m) => m.periode)).size === 1 ? ` · ${lijst[0].periode}` : ''}</Typography>}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' }, gap: 1 }}>
      {lijst.map((m) => (
        <Box key={m.id} sx={{ p: 1.25, borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff', borderLeft: `4px solid ${(STATUS_KLEUR[m.status] || STATUS_KLEUR.leeg).fg}` }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
            <Tooltip title={m.definitie || ''}><Typography sx={{ fontSize: 12, fontWeight: 700, color: '#475569', lineHeight: 1.3 }}>{m.naam}</Typography></Tooltip>
            <Stoplicht status={m.status} />
          </Stack>
          {CIJFER_UITLEG[m.sleutel] && <Typography sx={{ fontSize: 11.5, color: '#64748b', lineHeight: 1.35, mt: 0.25 }}>{CIJFER_UITLEG[m.sleutel]}</Typography>}
          {bewerk[m.id] !== undefined ? (
            <Stack direction="row" spacing={0.5} sx={{ mt: 0.75 }}>
              <TextField size="small" type="number" autoFocus value={bewerk[m.id]} onChange={(e) => setBewerk((p) => ({ ...p, [m.id]: e.target.value }))}
                onKeyDown={async (e) => { if (e.key === 'Enter') { await onWaarde(m, m.periode, bewerk[m.id]); setBewerk((p) => { const q = { ...p }; delete q[m.id]; return q; }); } if (e.key === 'Escape') setBewerk((p) => { const q = { ...p }; delete q[m.id]; return q; }); }}
                inputProps={{ step: 'any' }} sx={{ width: 120 }} />
              <Button size="small" onClick={async () => { await onWaarde(m, m.periode, bewerk[m.id]); setBewerk((p) => { const q = { ...p }; delete q[m.id]; return q; }); }} sx={{ textTransform: 'none', fontWeight: 700 }}>Ok</Button>
            </Stack>
          ) : (
            <Typography onClick={() => magInvullen && m.handmatig && setBewerk((p) => ({ ...p, [m.id]: m.waarde ?? '' }))}
              title={isOudeWaarde(m, bijwerken, bijwerkenSinds) ? 'Stand van de vorige berekening — wordt bijgewerkt' : undefined}
              sx={{ fontSize: 22, fontWeight: 800, mt: 0.5, cursor: magInvullen && m.handmatig ? 'text' : 'default', color: m.waarde == null ? '#94a3b8' : '#0f172a', opacity: isOudeWaarde(m, bijwerken, bijwerkenSinds) ? 0.45 : 1 }}>
              {m.waarde == null ? (m.handmatig && magInvullen ? 'invullen…' : (m.handmatig ? '–' : (m.snapshot?.melding || (bijwerken ? 'wordt berekend…' : 'nog niet berekend')))) : formatWaarde(m.waarde, m.eenheid)}
              {m.waarde != null && <Trend m={m} />}
            </Typography>
          )}
          {(m.voortgang || m.vorige?.waarde != null) ? <WeekVoortgang m={m} /> : (() => { const r = (m.reeks || []).filter((x) => x.waarde != null && x.periode !== m.periode); const v = r[r.length - 1]; return v ? <Typography sx={{ fontSize: 11.5, color: '#64748b' }}>vorige {v.periode.includes('W') ? 'week' : 'maand'}: <b>{formatWaarde(v.waarde, m.eenheid)}</b></Typography> : null; })()}
          {m.waarde != null && m.snapshot?.toelichting && <Typography sx={{ fontSize: 11.5, color: '#64748b' }}>{m.snapshot.toelichting}</Typography>}
          {Array.isArray(m.snapshot?.komende) && m.snapshot.komende.length > 0 && (
            <Box>
            <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: '#64748b', mt: 0.75 }}>OHW per maand</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.5, mt: 0.25, mb: 0.25 }}>
              {m.snapshot.komende.map((km) => { const st = ohwStatus(km); const pct = ohwPct(km); const later = km.periode > m.snapshot.komende[0].periode; return (
                <Box key={km.periode} data-testid="ohw-maand" data-status={st} sx={{ p: 0.5, borderRadius: 1, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', ...(st !== 'leeg' ? { borderLeft: `3px solid ${STATUS_KLEUR[st].fg}` } : {}) }}>
                  <Typography sx={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', color: '#94a3b8' }}>{maandLabel(km.periode)}</Typography>
                  {km.bbi != null
                    ? <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: '#0f172a' }}>{formatWaarde(km.bbi, 'eur')}</Typography>
                    : <Tooltip title={km.uitleg || 'Deze maand staat (nog) niet in het OHW-werkboek'}><Typography aria-label={`${maandLabel(km.periode)} ontbreekt`} sx={{ fontSize: 12.5, fontWeight: 800, color: '#94a3b8', cursor: 'help', width: 'fit-content' }}>–</Typography></Tooltip>}
                  <Typography sx={{ fontSize: 10.5, color: '#64748b' }}>{km.target_bbi != null ? `target ${formatWaarde(km.target_bbi, 'eur')}` : ''}{pct != null && <b style={{ color: STATUS_KLEUR[st].fg }}>{km.target_bbi != null ? ' · ' : ''}{Math.round(pct)}%</b>}</Typography>
                  {later && km.bbi != null && <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>nog in te vullen</Typography>}
                </Box>
              ); })}
            </Box>
            </Box>
          )}
          {m.snapshot?.jaar_n != null && (
            <Typography onClick={() => setEvalOpen(m)} sx={{ fontSize: 11.5, color: '#4338ca', cursor: 'pointer', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}>
              {m.snapshot.n || 0} evaluatie{m.snapshot.n === 1 ? '' : 's'} deze maand · jaar {formatWaarde(m.snapshot.jaar_gem, 'score')} ({m.snapshot.jaar_n}){m.snapshot.onder_4 ? ` · ${m.snapshot.onder_4} onder de 4` : ''} →
            </Typography>
          )}
          <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>{new Set(lijst.map((x) => x.periode)).size > 1 ? `${m.periode} · ` : ''}{m.doel_tekst || (m.doel != null ? `norm ${formatWaarde(m.doel, m.eenheid)}` : 'volgen')}{m.handmatig ? (m.eigenaar_email ? ` · ${naam(m.eigenaar_email)}` : '') : berekendLabel(m.berekend_at)}</Typography>
        </Box>
      ))}
          </Box>
        </Box>
      ))}
      <EvaluatiesDialog open={!!evalOpen} onClose={() => setEvalOpen(null)} snapshot={evalOpen?.snapshot} periode={evalOpen?.periode} />
    </Stack>
  );
}

/** Aanwezigheid: standaard iedereen aanwezig; de voorzitter vinkt afwezigen uit. */
export function AanwezigheidChips({ deelnemers, aanwezigheid, onZet, magLeiden }) {
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {(deelnemers || []).map((e) => {
        const st = aanwezigheid?.[e] || 'aanwezig';
        const aan = st !== 'afwezig';
        return (
          <Chip key={e} size="small" label={naam(e)} onClick={magLeiden ? () => onZet(e, aan ? 'afwezig' : 'aanwezig') : undefined}
            sx={{ height: 24, fontWeight: 700, bgcolor: aan ? '#dcfce7' : '#f1f5f9', color: aan ? '#166534' : '#94a3b8', textDecoration: aan ? 'none' : 'line-through', cursor: magLeiden ? 'pointer' : 'default' }} />
        );
      })}
    </Stack>
  );
}

export function HeadlineLijst({ headlines, onNieuw, onVerwijder, magSchrijven }) {
  const [tekst, setTekst] = useState('');
  const [soort, setSoort] = useState('klant');
  const [goed, setGoed] = useState('goed');
  const voegToe = async () => { if (!tekst.trim()) return; await onNieuw({ tekst: tekst.trim(), soort, goed_slecht: goed }); setTekst(''); };
  return (
    <Box>
      <Stack spacing={0.5}>
        {(headlines || []).map((h) => (
          <Stack key={h.id} direction="row" alignItems="center" spacing={1} sx={{ px: 1, py: 0.5, borderRadius: 1.5, bgcolor: '#fff', border: '1px solid #e2e8f0' }}>
            <Typography sx={{ fontSize: 15 }}>{h.goed_slecht === 'goed' ? '👍' : '👎'}</Typography>
            <Typography sx={{ flex: 1, fontSize: 13.5 }}>{h.tekst} <Box component="span" sx={{ fontSize: 11, color: '#94a3b8' }}>{h.soort} · {naam(h.door_email)}</Box></Typography>
            {magSchrijven && onVerwijder && <IconButton size="small" onClick={() => onVerwijder(h)}><DeleteOutlineIcon sx={{ fontSize: 16 }} /></IconButton>}
          </Stack>
        ))}
        {(!headlines || headlines.length === 0) && <Typography sx={{ fontSize: 13, color: '#94a3b8', px: 1 }}>Nog geen headlines: kort goed en slecht nieuws van klanten en team.</Typography>}
      </Stack>
      {magSchrijven && (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mt: 1.25 }} alignItems={{ md: 'center' }}>
          <TextField size="small" placeholder="Headline (één zin)" value={tekst} onChange={(e) => setTekst(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') voegToe(); }} sx={{ flex: 1 }} />
          <TextField select size="small" value={soort} onChange={(e) => setSoort(e.target.value)} sx={{ width: 110 }}><MenuItem value="klant">Klant</MenuItem><MenuItem value="team">Team</MenuItem></TextField>
          <Stack direction="row" spacing={0} sx={{ border: '1px solid #cbd5e1', borderRadius: 1, overflow: 'hidden' }}>
            <Button size="small" onClick={() => setGoed('goed')} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 0, px: 1.5, bgcolor: goed === 'goed' ? '#dcfce7' : 'transparent', color: goed === 'goed' ? '#166534' : '#64748b' }}>👍 Goed</Button>
            <Button size="small" onClick={() => setGoed('slecht')} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 0, px: 1.5, bgcolor: goed === 'slecht' ? '#fee2e2' : 'transparent', color: goed === 'slecht' ? '#991b1b' : '#64748b' }}>👎 Slecht</Button>
          </Stack>
          <Button size="small" variant="outlined" disabled={!tekst.trim()} onClick={voegToe} sx={{ textTransform: 'none', fontWeight: 700 }}>Toevoegen</Button>
        </Stack>
      )}
    </Box>
  );
}

/** Rocks van het lopende kwartaal. Met `beheer` (MT-lid + strategy:edit) ook toevoegen, bewerken en verwijderen,
 * met dezelfde rechten als de Strategie-pagina; zonder beheer alleen een link naar de Strategie-pagina. */
export function RocksLijst({ rocks, magStrategie, beheer }) {
  const knopNieuw = beheer?.onNieuw && (
    <Button size="small" onClick={beheer.onNieuw} sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 700 }}>＋ Rock toevoegen</Button>
  );
  if (!rocks || rocks.length === 0) {
    return (
      <Stack spacing={0.5}>
        <Typography sx={{ fontSize: 13, color: '#94a3b8' }}>Geen open Rocks (of geen pijler gekoppeld). Rocks staan op de Strategie-pagina.</Typography>
        {knopNieuw}
      </Stack>
    );
  }
  return (
    <Stack spacing={0.5}>
      {rocks.map((r) => (
        <Stack key={r.id} direction="row" alignItems="center" spacing={1} sx={{ px: 1, py: 0.5, borderRadius: 1.5, bgcolor: '#fff', border: '1px solid #e2e8f0', borderLeft: `4px solid ${r.off_track ? '#dc2626' : '#16a34a'}` }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{r.titel}</Typography>
            <Typography sx={{ fontSize: 11.5, color: '#64748b' }}>{naam(r.eigenaar_email)}{r.kwartaal ? ` · ${r.kwartaal}` : ''}{r.pijler ? ` · ${r.pijler}` : ''}{r.laatste_update ? ` · ${r.laatste_update}` : ''}</Typography>
          </Box>
          <Chip size="small" label={r.off_track ? 'off track' : 'on track'} sx={{ height: 20, fontSize: 11, fontWeight: 800, bgcolor: r.off_track ? '#fee2e2' : '#dcfce7', color: r.off_track ? '#991b1b' : '#166534' }} />
          <Typography sx={{ fontSize: 12, color: '#94a3b8', width: 36, textAlign: 'right' }}>{r.progress_pct}%</Typography>
          {beheer && (
            <Tooltip title="Bewerken"><IconButton size="small" aria-label={`Bewerk Rock ${r.titel}`} onClick={() => beheer.onBewerk(r)}><EditOutlinedIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
          )}
          {beheer && (
            <Tooltip title="Verwijderen"><IconButton size="small" aria-label={`Verwijder Rock ${r.titel}`} onClick={() => beheer.onVerwijder(r)}><DeleteOutlineIcon sx={{ fontSize: 16, color: '#94a3b8' }} /></IconButton></Tooltip>
          )}
          {!beheer && magStrategie && <Button size="small" href="/strategy" sx={{ textTransform: 'none', fontWeight: 700, minWidth: 0 }}>Bijwerken</Button>}
        </Stack>
      ))}
      {knopNieuw}
    </Stack>
  );
}

/** Invulblok volgens prep_schema: secties van soort lijst (kolommen), regels (één per regel) of tekst. */
export function PrepFormulier({ schema, waarde, onChange, onIndienen, personen, ingediend, alleenLezen = false }) {
  const v = waarde || {};
  const secties = schema?.secties || [];
  if (secties.length === 0) return <Typography sx={{ fontSize: 13, color: '#94a3b8' }}>Dit overleg heeft geen invulblok.</Typography>;
  const zet = (sleutel, val) => onChange({ ...v, [sleutel]: val });
  return (
    <Stack spacing={2}>
      {secties.map((s) => {
        if (s.soort === 'tekst') {
          return <TextField key={s.sleutel} label={s.label} size="small" fullWidth multiline minRows={2} value={v[s.sleutel] || ''} onChange={(e) => zet(s.sleutel, e.target.value)} disabled={alleenLezen} />;
        }
        if (s.soort === 'regels') {
          const regels = Array.isArray(v[s.sleutel]) ? v[s.sleutel] : [];
          return (
            <Box key={s.sleutel}>
              <Typography sx={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748b', mb: 0.5 }}>{s.label}</Typography>
              <Stack spacing={0.5}>
                {regels.map((r, i) => (
                  <Stack key={i} direction="row" spacing={0.5}>
                    <TextField size="small" fullWidth value={r} onChange={(e) => zet(s.sleutel, regels.map((x, j) => (j === i ? e.target.value : x)))} disabled={alleenLezen} />
                    {!alleenLezen && <IconButton size="small" onClick={() => zet(s.sleutel, regels.filter((_, j) => j !== i))}><DeleteOutlineIcon sx={{ fontSize: 16 }} /></IconButton>}
                  </Stack>
                ))}
                {!alleenLezen && regels.length < (s.max || 10) && <Button size="small" onClick={() => zet(s.sleutel, [...regels, ''])} sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 700 }}>＋ regel</Button>}
              </Stack>
            </Box>
          );
        }
        const rijen = Array.isArray(v[s.sleutel]) ? v[s.sleutel] : [];
        const velden = s.velden || [];
        return (
          <Box key={s.sleutel}>
            <Typography sx={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748b', mb: 0.5 }}>{s.label}</Typography>
            <Stack spacing={0.75}>
              {rijen.map((rij, i) => (
                <Box key={i} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: `repeat(${velden.length}, 1fr) 32px` }, gap: 0.75, alignItems: 'center', p: 1, borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                  {velden.map((f) => {
                    const val = rij[f.k];
                    const upd = (nv) => zet(s.sleutel, rijen.map((x, j) => (j === i ? { ...x, [f.k]: nv } : x)));
                    if (f.type === 'bool') return <FormControlLabel key={f.k} sx={{ m: 0 }} control={<Checkbox size="small" checked={!!val} onChange={(e) => upd(e.target.checked)} disabled={alleenLezen} />} label={<Typography variant="caption">{f.label}</Typography>} />;
                    if (f.type === 'persoon') return <PersoonSelect key={f.k} personen={personen} value={val || ''} onChange={upd} label={f.label} sx={{ minWidth: 0 }} />;
                    if (f.type === 'datum') return <TextField key={f.k} size="small" type="date" label={f.label} value={val || ''} onChange={(e) => upd(e.target.value)} InputLabelProps={{ shrink: true }} disabled={alleenLezen} />;
                    return <TextField key={f.k} size="small" label={f.label} value={val || ''} onChange={(e) => upd(e.target.value)} disabled={alleenLezen} />;
                  })}
                  {!alleenLezen && <IconButton size="small" onClick={() => zet(s.sleutel, rijen.filter((_, j) => j !== i))}><DeleteOutlineIcon sx={{ fontSize: 16 }} /></IconButton>}
                </Box>
              ))}
              {!alleenLezen && rijen.length < (s.max || 10) && <Button size="small" onClick={() => zet(s.sleutel, [...rijen, {}])} sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 700 }}>＋ {s.label.split(' ')[0] === 'Per' ? 'regel' : 'toevoegen'}</Button>}
            </Stack>
          </Box>
        );
      })}
      {!alleenLezen && onIndienen && (
        <Stack direction="row" spacing={1} alignItems="center">
          <Button variant="contained" disableElevation onClick={onIndienen} sx={{ textTransform: 'none', fontWeight: 700 }}>{ingediend ? 'Opnieuw indienen' : 'Indienen'}</Button>
          {ingediend && <Typography variant="caption" color="text.secondary">Ingediend {tijdKort(ingediend)} · {datumKort(ingediend)}</Typography>}
        </Stack>
      )}
    </Stack>
  );
}
